import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Alert,
  Button,
  Box,
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export interface AdminUserCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  error: string | null;
  onCreate: (data: { username: string; password: string; email?: string }) => Promise<void>;
  clearError: () => void;
}

export function AdminUserCreateDialog({
  open,
  onClose,
  onSuccess,
  error,
  onCreate,
  clearError,
}: AdminUserCreateDialogProps) {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [localError, setLocalError] = useState('');

  const handleSubmit = async () => {
    setLocalError('');
    clearError();

    const createData: { username: string; password: string; email?: string } = {
      username,
      password,
    };
    if (email) {
      createData.email = email;
    }

    try {
      await onCreate(createData);
      handleClose();
      onSuccess();
    } catch (err: any) {
      setLocalError(err.message || t('users.createFailed'));
    }
  };

  const handleClose = () => {
    setUsername('');
    setPassword('');
    setEmail('');
    setLocalError('');
    clearError();
    onClose();
  };

  const displayError = localError || error;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <LockIcon />
          {t('users.createTitle')}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {displayError && <Alert severity="error">{displayError}</Alert>}

          <TextField
            fullWidth
            label={t('users.username')}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="off"
          />

          <TextField
            fullWidth
            label={t('users.password')}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            helperText={t('users.passwordMinLength')}
          />

          <TextField
            fullWidth
            label={t('users.emailOptional')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('users.cancel')}</Button>
        <Button onClick={handleSubmit} variant="contained">
          {t('users.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
